import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "score-box"
}
const _hoisted_2 = {
  key: 1,
  class: "score-box"
}
const _hoisted_3 = {
  key: 0,
  class: "score-box weighted-score grade"
}
const _hoisted_4 = ["onClick"]
const _hoisted_5 = ["onClick"]
const _hoisted_6 = {
  key: 3,
  class: "score-box weighted-score"
}
const _hoisted_7 = { key: 0 }
const _hoisted_8 = { key: 1 }
const _hoisted_9 = { key: 2 }
const _hoisted_10 = { key: 3 }
const _hoisted_11 = {
  key: 4,
  class: "calculation-details"
}
const _hoisted_12 = { class: "calculation-table" }
const _hoisted_13 = { class: "total-row" }
const _hoisted_14 = { colspan: "3" }
const _hoisted_15 = {
  key: 0,
  class: "elective-requirements"
}
const _hoisted_16 = { class: "elective-table" }
const _hoisted_17 = { class: "elective-group" }
const _hoisted_18 = { class: "elective-options" }
const _hoisted_19 = { key: 0 }

import { defineComponent, ref, reactive, computed, watch } from 'vue'

// icons
import { checkmark, close, ellipseOutline, triangleOutline, chevronDown, chevronUp, informationCircleOutline } from 'ionicons/icons';

// components
import { IonCol, IonIcon, IonRow, IonPopover, IonContent, IonItem, IonLabel } from '@ionic/vue';
import UserProfileFormModal from '@/components/modals/UserProfileFormModal.vue';

// composables
import { utils } from '@/composables/utils';
import { useStore } from '@/store';

// types
import { School } from '@/types';

// services
import AchieveJUPASService from '@/services/AchieveJUPASService';


export default /*@__PURE__*/_defineComponent({
  __name: 'SubjectScoreInputSection',
  props: ["targetUser", "program", "fromDeckView", "title", "passIcon", "hideSubjectNames",
                            "readonly", "noBottomBorderRadius", "noTopBorderRadius", "gradeSelectLargeFont", "source"],
  emits: ['targetScoresChanged'],
  setup(__props, { emit: __emit }) {

const emit = __emit;
const props = __props;
const { openModal, openServiceModal, getWeightedScore, getUserSubjects,
        calculateSumWeightedScores, getAvailableScoreType, getScoreDiff,
        isMeetUniEntranceRequirements, isMeetProgramElectiveRequirements, } = utils();

// Store
const store = useStore();
const user = computed(() => props.targetUser || store.state.user);
const userRelatedSchool = computed<School>(() => store.getters.userRelatedSchool);

// Constants (subjects & grades)
const allSubjects = computed(() => store.state.allSubjects);
const grades = ['5**', '5*', '5', '4', '3', '2', '1', 'U'];

// Get user's studying subjects with short names
const userSubjects = computed(() => {
  const userObj = props.targetUser || user.value;
  return getUserSubjects(allSubjects.value, userObj);
});

// Subject Grade (target / school prediction)
const getUserSubjectGrades = () => {
  const userObj = props.targetUser || user.value;
  return userObj.userSubjectGrades || [];
};
const getSubjectGrade = (subjectId: any) => {
  if (props.source == 'schoolPredict' && !userRelatedSchool.value.showPredictedScoresToStudents && props.readonly) {
    return '✱'; // not visible to students (requirements from school e.g. TWGHKYDS)
  }
  const res = getUserSubjectGrades().find(usg => usg.subjectId == subjectId && usg.source == props.source);
  return res?.grade;
};

// Target scores
const targetScores = reactive({
  chi: '',
  eng: '',
  math: '',
  csd: 'Attained',
});
const currPopoverSubjectId = ref('');
const setTargetScore = (grade) => {
  const subjectId = currPopoverSubjectId.value;
  const userSubjectGrades = getUserSubjectGrades();
  const updatedObj = userSubjectGrades.find(usg => usg.subjectId == subjectId && usg.source == props.source) ||
                    { subjectId, grade, source: props.source };
  updatedObj.userId = props.targetUser?.id || user.value.id; // may be for specific user
  updatedObj.grade = grade;
  updatedObj.updatedBy = user.value.id;
  AchieveJUPASService.upsertUserSubjectGrades([updatedObj]);

  // Update store props
  const targetUserObj = props.targetUser || user.value;
  const usg = targetUserObj.userSubjectGrades.find(usg => usg.subjectId == subjectId && usg.source == props.source);
  if (usg) usg.grade = grade;
  else {
    if (!Array.isArray(targetUserObj.userSubjectGrades)) targetUserObj.userSubjectGrades = [];
    targetUserObj?.userSubjectGrades.push(updatedObj);
  }
  //store.commit('upsertUserSubjectGrades', [updatedObj]); // Update user's own target scores
  emit('targetScoresChanged', targetScores);
};

// Popover
const popoverState = reactive({
  seats: false,
  grades: false,
});
const popoverEvent = ref();
const setPopoverOpen = (popoverKey: any, state: boolean, ev: any, currSelectedSubjectId?: any) => {
  popoverEvent.value = ev;
  popoverState[popoverKey] = state;
  currPopoverSubjectId.value = currSelectedSubjectId
};

// Calculation details accordion
const calculationDetailsOpen = ref(false);
const toggleCalculationDetails = () => {
  calculationDetailsOpen.value = !calculationDetailsOpen.value;
};
const calculationResults = computed(() => calculateSumWeightedScores(allSubjects.value, userSubjects.value, getUserSubjectGrades(), props.program, props.source));
const calculationSteps = computed(() => calculationResults.value.calculationSteps.slice());
const notMeetProgramMinGradeRequirements = computed(() => calculationResults.value.notMeetMinGradeSubjects?.length > 0);
const passUniEntranceRequirements = computed(() => isMeetUniEntranceRequirements(getUserSubjectGrades().filter(sg => sg.source == props.source)));
const passProgramElectiveRequirements = computed(() => isMeetProgramElectiveRequirements(props.program, allSubjects.value, userSubjects.value, getUserSubjectGrades(), props.source));
const groupedMinGradeSubjectRules = computed(() => calculationResults.value.groupedMinGradeSubjectRules); // min grade requirements for elective 1 & 2 (failed to achieve)

// Get subject name from ID
const getSubjectName = (subjectId: number) => {
  const subject = allSubjects.value.find(s => s.id === subjectId);
  return subject ? subject.shortName : 'Unknown';
};

// Display weighted score for any subject (default latest year)
const calculateBest5Scores = (targetRuleYear: any = null) => {
  const result = calculateSumWeightedScores(allSubjects.value, userSubjects.value, getUserSubjectGrades(), props.program, props.source, targetRuleYear);
  return result.totalScores.toFixed(1);
};

const getColorForScore = () => {
  if (notMeetProgramMinGradeRequirements.value) return 'danger';
  if (!passProgramElectiveRequirements.value) return 'medium';
  if (!passUniEntranceRequirements.value) return 'danger';
  const scoreDiff = getScoreDiff(props.program, allSubjects.value, userSubjects.value, getUserSubjectGrades(), props.source);
  if (scoreDiff == null) return 'light';
  if (scoreDiff >= 0) return 'darkgreen';
  //else if (scoreDiff >= -2) return 'warning'; // TBC (PTCC VPr. Chan prefers this)
  return 'danger';
};

// Edit Subject (Electives)
const checkOpenEditSubjectModal = async (subject) => {
  if (!props.targetUser && !["Chinese Language", "English Language", "Mathematics"].includes(subject.name)) {
    await openModal(UserProfileFormModal, { fromAchieveJUPAS: true });
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["target-scores-section", { 'grade-select-large-font': __props.gradeSelectLargeFont }]),
    style: _normalizeStyle({
      'border-bottom-left-radius': __props.noBottomBorderRadius ? '0' : '18px',
      'border-bottom-right-radius': __props.noBottomBorderRadius ? '0' : '18px',
      'border-top-left-radius': __props.noTopBorderRadius ? '0' : '18px',
      'border-top-right-radius': __props.noTopBorderRadius ? '0' : '18px',
  })
  }, [
    _createVNode(_unref(IonRow), { class: "ion-text-center subject-scores-row" }, {
      default: _withCtx(() => [
        _createVNode(_unref(IonCol), {
          size: "12",
          style: {"background":"#2f2f2f","color":"#fff"}
        }, {
          default: _withCtx(() => [
            (!__props.passIcon)
              ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(__props.title) + "  ", 1))
              : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _createTextVNode(_toDisplayString(__props.title) + "   ", 1),
                  _createVNode(_unref(IonIcon), {
                    color: "success",
                    icon: __props.passIcon,
                    size: "small"
                  }, null, 8, ["icon"]),
                  _cache[2] || (_cache[2] = _createTextVNode("Pass   ")),
                  _createVNode(_unref(IonIcon), {
                    color: "danger",
                    icon: __props.passIcon,
                    size: "small"
                  }, null, 8, ["icon"]),
                  _cache[3] || (_cache[3] = _createTextVNode("Not Pass  "))
                ]))
          ]),
          _: 1
        }),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(userSubjects.value, (subject) => {
          return (_openBlock(), _createBlock(_unref(IonCol), {
            key: subject.shortName,
            class: "subject-col"
          }, {
            default: _withCtx(() => [
              (__props.readonly)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(getSubjectGrade(subject.id) || '-'), 1))
                : (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: "score-box grade-select",
                    onClick: ($event: any) => (setPopoverOpen('grades', true, $event, subject.id))
                  }, _toDisplayString(getSubjectGrade(subject.id) || '-'), 9, _hoisted_4)),
              (!__props.hideSubjectNames)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 2,
                    class: "score-box subject-name",
                    onClick: ($event: any) => (checkOpenEditSubjectModal(subject))
                  }, _toDisplayString(subject.shortName), 9, _hoisted_5))
                : _createCommentVNode("", true),
              (__props.fromDeckView)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(getSubjectGrade(subject.id) ? _unref(getWeightedScore)(__props.program, subject.id, getSubjectGrade(subject.id)).weightedScore : '0'), 1))
                : _createCommentVNode("", true)
            ]),
            _: 2
          }, 1024))
        }), 128))
      ]),
      _: 1
    }),
    (__props.fromDeckView)
      ? (_openBlock(), _createBlock(_unref(IonRow), {
          key: 0,
          style: {"font-size":"0.85em","color":"#fff"}
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(IonCol), {
              style: _normalizeStyle([{"align-content":"center","background":"#656565"}, { 'background-color': `var(--ion-color-${getColorForScore()}` }])
            }, {
              default: _withCtx(() => [
                _createElementVNode("b", null, _toDisplayString(calculateBest5Scores()), 1),
                _cache[7] || (_cache[7] = _createElementVNode("br", null, null, -1)),
                (notMeetProgramMinGradeRequirements.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_7, " Not meet program entrance requirements "))
                  : (!passProgramElectiveRequirements.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_8, " Not meet program elective requirements "))
                    : (!passUniEntranceRequirements.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_9, " Not meet minimum university entrance requirements (332) "))
                      : (_unref(getAvailableScoreType)(__props.program) != 'N/A')
                        ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(getColorForScore() == 'danger' ? 'Not pass' : 'Pass') + " the program's " + _toDisplayString(_unref(getAvailableScoreType)(__props.program)), 1))
                        : _createCommentVNode("", true),
                _createElementVNode("div", {
                  class: "calculation-details-toggle",
                  onClick: toggleCalculationDetails
                }, [
                  _createVNode(_unref(IonIcon), {
                    icon: calculationDetailsOpen.value ? _unref(chevronUp) : _unref(chevronDown)
                  }, null, 8, ["icon"]),
                  _createElementVNode("span", null, _toDisplayString(calculationDetailsOpen.value ? 'Hide calculation details' : 'Show calculation details'), 1)
                ]),
                (calculationDetailsOpen.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                      _createElementVNode("table", _hoisted_12, [
                        _cache[5] || (_cache[5] = _createElementVNode("thead", null, [
                          _createElementVNode("tr", null, [
                            _createElementVNode("th", null, "#"),
                            _createElementVNode("th", null, "Subject"),
                            _createElementVNode("th", null, "Min grade"),
                            _createElementVNode("th", null, "Your grade"),
                            _createElementVNode("th", null, "Score"),
                            _createElementVNode("th", null, "Weight"),
                            _createElementVNode("th", null, "Weighted score")
                          ])
                        ], -1)),
                        _createElementVNode("tbody", null, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calculationSteps.value, (step, index) => {
                            return (_openBlock(), _createElementBlock("tr", {
                              key: index,
                              style: _normalizeStyle({ background: step.missingSubjects ? `var(--ion-color-danger)` : (step.scoreObj?.failedMinGrade ? `var(--ion-color-medium)` : undefined) })
                            }, [
                              _createElementVNode("td", null, _toDisplayString(index + 1), 1),
                              _createElementVNode("td", null, _toDisplayString(step.missingSubjects ? step.missingSubjects.join('/') : (step.bonusPercent ? 'Bonus' : getSubjectName(step.scoreObj?.subjectId))), 1),
                              _createElementVNode("td", null, _toDisplayString(step.scoreObj?.minGrade || '-'), 1),
                              _createElementVNode("td", null, _toDisplayString(step.scoreObj?.grade || '-'), 1),
                              _createElementVNode("td", null, _toDisplayString(step.scoreObj?.score || '-'), 1),
                              _createElementVNode("td", null, _toDisplayString(step.bonusPercent || step.scoreObj?.weighting || (step.multiplier ? step.multiplier : '1')), 1),
                              _createElementVNode("td", null, _toDisplayString(step.weightedScore || (step.scoreObj?.weightedScore ? (step.scoreObj?.weightedScore * (step.multiplier || 1)).toFixed(1) : '-')), 1)
                            ], 4))
                          }), 128)),
                          _createElementVNode("tr", _hoisted_13, [
                            _cache[4] || (_cache[4] = _createElementVNode("td", {
                              colspan: "4",
                              class: "total-label"
                            }, "Total", -1)),
                            _createElementVNode("td", _hoisted_14, _toDisplayString(calculateBest5Scores()), 1)
                          ])
                        ])
                      ]),
                      (Object.keys(groupedMinGradeSubjectRules.value).length > 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                            _createElementVNode("table", _hoisted_16, [
                              _cache[6] || (_cache[6] = _createElementVNode("thead", null, [
                                _createElementVNode("tr", null, [
                                  _createElementVNode("th", null, "Elective Group"),
                                  _createElementVNode("th", null, "Minimum Requirements")
                                ])
                              ], -1)),
                              _createElementVNode("tbody", null, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupedMinGradeSubjectRules.value, (rules, groupName) => {
                                  return (_openBlock(), _createElementBlock("tr", { key: groupName }, [
                                    _createElementVNode("td", _hoisted_17, _toDisplayString(groupName), 1),
                                    _createElementVNode("td", _hoisted_18, [
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rules, (rule, index) => {
                                        return (_openBlock(), _createElementBlock("span", { key: index }, [
                                          _createTextVNode(_toDisplayString(getSubjectName(rule.subjectId)) + " (Min: " + _toDisplayString(rule.minGrade) + ")", 1),
                                          (index < rules.length - 1)
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_19, " or "))
                                            : _createCommentVNode("", true)
                                        ]))
                                      }), 128))
                                    ])
                                  ]))
                                }), 128))
                              ])
                            ])
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _: 1,
              __: [7]
            }, 8, ["style"])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(_unref(IonPopover), {
      isOpen: popoverState.grades,
      event: popoverEvent.value,
      "dismiss-on-select": true,
      onDidDismiss: _cache[1] || (_cache[1] = ($event: any) => (setPopoverOpen('grades', false, $event)))
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(IonContent), null, {
          default: _withCtx(() => [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(grades, (grade) => {
              return _createVNode(_unref(IonItem), {
                lines: "full",
                key: grade,
                onClick: ($event: any) => (setTargetScore(grade))
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(IonLabel), null, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(grade), 1)
                    ]),
                    _: 2
                  }, 1024)
                ]),
                _: 2
              }, 1032, ["onClick"])
            }), 64)),
            _createVNode(_unref(IonItem), {
              lines: "full",
              button: "",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (setTargetScore('')))
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(IonLabel), null, {
                  default: _withCtx(() => _cache[8] || (_cache[8] = [
                    _createTextVNode("-")
                  ])),
                  _: 1,
                  __: [8]
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["isOpen", "event"])
  ], 6))
}
}

})